/**
 * Knowledge base detail — v2 route (Phase 3.2 / 7 M1). The URL stays
 * `/workspaces/:id`; only the user-facing name became "Knowledge base" in the
 * 2026-07 demo review.
 *
 * Two-tab shell (Documents / Knowledge graph) around the existing DocumentSet
 * machinery. A knowledge base IS a DocumentSet at the DB layer, so
 * `workspaceId` and `setId` are the same UUID. The old "Build Graph" tab was
 * folded into Documents as a "Build Knowledge Graph" section (same review) —
 * the build config is secondary to putting content in, and the everyday build
 * runs from Documents' Commit + Build. Skills likewise live inside Documents.
 *
 * URL: `/workspaces/:id?tab=documents|query` (`query` = the Knowledge graph tab)
 *
 * Phase 7 M1 additions:
 *   - Contextual status card with primary CTA that advances the wizard
 *   - Auto-attach all built-in skills on first draft (before first commit)
 *
 * The numbered stepper and the always-on Ask bar were removed once the Query
 * tab landed — the tab strip already shows where you are, and two query
 * surfaces on one page was one too many.
 */

import { fork } from '@cortex/utils/fork';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { BackButton } from '../../components/common/BackButton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { DocumentsTab } from '../../components/workspaces/DocumentsTab';
import { QueryTab } from '../../components/workspaces/QueryTab';
import { useWorkspaceEvents } from '../../hooks/useWorkspaceEvents';
import { useContentVersions, useWorkspace } from '../../hooks/useWorkspaces';

// `query` is the Knowledge graph tab's internal key (kept to avoid churning
// deep-links + the wizard state machine). The old `kg` (Build Graph) tab was
// folded into Documents, so legacy `?tab=kg` links fall through to Documents.
export type TWorkspaceTab = 'documents' | 'query';

export interface IWorkspaceSearch {
  tab?: TWorkspaceTab;
  /** KG version to pin on the Knowledge graph tab (deep-link from a version badge). */
  kgVersion?: number;
}

export const Route = createFileRoute('/workspaces/$id')({
  component: WorkspaceDetailPage,
  validateSearch: (s: Record<string, unknown>): IWorkspaceSearch => {
    const out: IWorkspaceSearch = {};
    if (s.tab === 'documents' || s.tab === 'query') {
      out.tab = s.tab;
    }
    const v = typeof s.kgVersion === 'string' ? Number(s.kgVersion) : s.kgVersion;
    if (typeof v === 'number' && Number.isFinite(v)) out.kgVersion = v;
    return out;
  },
});

/**
 * Which tab each wizard step navigates to when clicked. "Build KG" now points
 * at Documents (the build config lives there under Build Knowledge Graph).
 */
export const stepTabs: TWorkspaceTab[] = ['documents', 'documents', 'documents', 'query'];

/** Outbox events that change what the Documents tab renders. */
export const skillRunEventTypes = new Set([
  'skill.run.started',
  'skill.run.completed',
  'skill.run.failed',
  'artifact.produced',
]);

export interface IFlowStep {
  label: string;
  done: boolean;
}

/**
 * Pure wizard state machine: given what the workspace has, which of the four
 * steps are complete. Exported so the spec can exercise the real logic rather
 * than a copy of it.
 */
export function computeFlowSteps(state: {
  hasDraft: boolean;
  hasCommitted: boolean;
  hasSources: boolean;
  hasActiveKg: boolean;
}): IFlowStep[] {
  const { hasDraft, hasCommitted, hasSources, hasActiveKg } = state;
  return [
    { label: 'Upload', done: hasSources || hasDraft },
    { label: 'Commit', done: hasCommitted && !hasDraft },
    { label: 'Build KG', done: hasActiveKg },
    { label: 'Query', done: hasActiveKg },
  ];
}

/** Index of the first incomplete step, or -1 when the workspace is fully set up. */
export function currentStepIdxOf(steps: IFlowStep[]): number {
  return steps.findIndex((s) => !s.done);
}

function WorkspaceDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const nav = useNavigate();
  const qc = useQueryClient();
  const workspace = useWorkspace(id);
  const versions = useContentVersions(id);

  // SSE: keep the Documents tab live as skill runs progress. `artifact.produced`
  // and the terminal run events also touch the latest committed version, since
  // artifacts are written into it — that is what flips the file status pills
  // without a manual refresh.
  useWorkspaceEvents(id, (event) => {
    // KG build lifecycle: refetch versions the moment a build starts/ends so
    // the sync progress appears at once instead of waiting for a stale poll.
    // Owned at the shell so it works regardless of which tab is mounted.
    if (event.eventType.startsWith('kg.build.')) {
      fork(qc.invalidateQueries({ queryKey: ['document-set-kg-versions', id] }));
      return;
    }
    if (!skillRunEventTypes.has(event.eventType)) return;
    fork(qc.invalidateQueries({ queryKey: ['document-set-lineage', id] }));
    fork(qc.invalidateQueries({ queryKey: ['document-set-skill-runs', id] }));
    if (event.eventType !== 'skill.run.started') {
      fork(qc.invalidateQueries({ queryKey: ['document-set-version', id, 'latest'] }));
    }
  });

  const tab: TWorkspaceTab = search.tab ?? 'documents';
  function setTab(next: TWorkspaceTab): void {
    fork(
      nav({
        to: Route.to,
        params: { id },
        // `prev` is the router's merged search type (other routes contribute
        // keys like the mockup's `tab: 'skills'`); keep them and override tab.
        search: (prev) => ({ ...prev, tab: next }),
      }),
    );
  }

  // Jump to the Knowledge graph tab with a specific KG build pinned — wired to
  // the KG version badges in the Documents tab's Versions table. The explorer
  // reads `kgVersion` from search as its initial selection (deep-link wins).
  function openKgVersion(version: number): void {
    fork(
      nav({
        to: Route.to,
        params: { id },
        search: (prev) => ({ ...prev, tab: 'query', kgVersion: version }),
      }),
    );
  }

  // Auto-attach of built-in skills now lives in DocumentsTab, where the file
  // types are known — it attaches only the converters that match the dropped
  // files rather than all of them.

  if (workspace.isLoading) return <Spinner />;
  if (workspace.error)
    return (
      <div className="alert alert--danger">
        Failed to load: {(workspace.error as Error).message}
      </div>
    );
  if (!workspace.data)
    return (
      <EmptyState
        title="Knowledge base not found"
        description="It may have been deleted or the URL is stale."
        cta={<BackButton to="/workspaces" label="Back to knowledge bases" />}
      />
    );

  // Empty-workspace hero: when the workspace has no versions (no draft, no committed),
  // show a big dropzone instead of the tabbed interface.
  const hasNoVersions = !versions.isLoading && (versions.data?.length ?? 0) === 0;

  if (hasNoVersions) {
    return (
      <>
        <PageHeader
          title={workspace.data.name}
          actions={<BackButton to="/workspaces" label="Knowledge bases" />}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            padding: 'var(--sp-6)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 48,
              marginBottom: 'var(--sp-3)',
              opacity: 0.3,
            }}
          >
            📄
          </div>
          <h2 style={{ margin: '0 0 var(--sp-2)', fontSize: 18, fontWeight: 'var(--fw-med)' }}>
            Drop files to start
          </h2>
          <p
            className="t-muted"
            style={{ fontSize: 13, maxWidth: 400, marginBottom: 'var(--sp-3)' }}
          >
            Upload documents to create your first content version. Built-in skills will
            automatically convert them for the knowledge graph.
          </p>
          <div
            style={{
              padding: 'var(--sp-4)',
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-md, 8px)',
              background: 'var(--surface)',
              width: '100%',
              maxWidth: 500,
            }}
          >
            <p className="t-muted" style={{ fontSize: 12 }}>
              Switch to the Documents tab below to upload files, or drag and drop files anywhere on
              this page.
            </p>
          </div>
        </div>
        <nav className="tabs" style={{ marginBottom: 'var(--sp-3)' }}>
          <button
            type="button"
            className={`tab${tab === 'documents' ? ' is-active' : ''}`}
            onClick={() => setTab('documents')}
          >
            Documents
          </button>
          <button
            type="button"
            className={`tab${tab === 'query' ? ' is-active' : ''}`}
            onClick={() => setTab('query')}
          >
            Knowledge graph
          </button>
        </nav>
        <div style={{ display: tab === 'documents' ? 'block' : 'none' }}>
          <DocumentsTab workspaceId={id} onOpenKgVersion={openKgVersion} />
        </div>
        {/* Mounted only while active — the graph explorer fires its own
            queries, and unlike Documents it has no staged state to preserve. */}
        {tab === 'query' ? <QueryTab workspaceId={id} kgVersion={search.kgVersion} /> : null}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={workspace.data.name}
        actions={<BackButton to="/workspaces" label="Knowledge bases" />}
      />

      <nav className="tabs" style={{ marginBottom: 'var(--sp-3)' }}>
        <button
          type="button"
          className={`tab${tab === 'documents' ? ' is-active' : ''}`}
          onClick={() => setTab('documents')}
        >
          Documents
        </button>
        <button
          type="button"
          className={`tab${tab === 'query' ? ' is-active' : ''}`}
          onClick={() => setTab('query')}
        >
          Knowledge graph
        </button>
      </nav>

      {/* Documents stays mounted so local state (staged uploads, form fields)
          survives tab switches; CSS display hides it when the graph is open. */}
      <div style={{ display: tab === 'documents' ? 'block' : 'none' }}>
        <DocumentsTab workspaceId={id} onOpenKgVersion={openKgVersion} />
      </div>
      {/* Mounted only while active — the graph explorer fires its own queries,
          and unlike Documents it has no staged state to preserve. */}
      {tab === 'query' ? <QueryTab workspaceId={id} kgVersion={search.kgVersion} /> : null}
    </>
  );
}
